import { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,

  CardContent,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Divider,
  Stack,
  Tooltip,
  Pagination,
  CardMedia,
} from '@mui/material';
import { getApiUrl } from '../data/api';
import { useQuery } from '@tanstack/react-query';
import begardSealImage from '../assets/products/begard-seal-of-approval.png';
import { useAccessibility } from '../contexts/AccessibilityContext';
import SEO from '../components/SEO';
import { generatePageSEO, generateBreadcrumbData } from '../utils/seoUtils';

type Product = {
  id: number;
  name: string;
  variants: number;
  price: number;
  thumbnail_url: string;
  is_ignored: boolean;
  external_id: string;
  begard_approved?: boolean;
  // Add these if you expect them from your API or want to keep the filters:
  category?: string;
  audience?: string;
  // Variant color approvals information
  approved_colors?: string[];
  // Whether the product is currently for sale
  for_sale?: boolean;
};

// Helper to capitalize first letter and handle special cases
const formatDropdownLabel = (value: string) => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial values from URL parameters or default to 'all'
  const [selectedCategory, setSelectedCategory] = useState<string>(
    searchParams.get('category') || 'all'
  );
  const [selectedAudience, setSelectedAudience] = useState<string>(
    searchParams.get('audience') || 'all'
  );
  const [page, setPage] = useState<number>(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const ITEMS_PER_PAGE = 12;
  const { announce } = useAccessibility();
  
  // SEO data
  const seoData = generatePageSEO('products');
  
  const breadcrumbData = generateBreadcrumbData([
    { name: 'Home', url: '/' },
    { name: 'Products', url: '/products' }
  ]);

  // React Query for products
  const {
    data,
    isLoading: loading,
    error,
  } = useQuery<{ products: Product[] }, Error>({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await fetch(`${getApiUrl()}/products`);
      if (!res.ok) throw new Error('Failed to fetch products');
      return res.json() as Promise<{ products: Product[] }>;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const products: Product[] = data?.products || [];

  // Sync state with URL parameters when they change
  useEffect(() => {
    const categoryParam = searchParams.get('category') || 'all';
    const audienceParam = searchParams.get('audience') || 'all';
    const pageParam = parseInt(searchParams.get('page') || '1', 10);
    
    setSelectedCategory(categoryParam);
    setSelectedAudience(audienceParam);
    setPage(pageParam);
  }, [searchParams]);

  // Helper function to update URL parameters
  const updateFilters = (category?: string, audience?: string, newPage?: number) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (category !== undefined) {
      if (category === 'all') {
        newParams.delete('category');
      } else {
        newParams.set('category', category);
      }
    }
    
    if (audience !== undefined) {
      if (audience === 'all') {
        newParams.delete('audience');
      } else {
        newParams.set('audience', audience);
      }
    }
    
    if (newPage !== undefined) {
      if (newPage === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', newPage.toString());
      }
    }
    
    setSearchParams(newParams);
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    const newCategory = event.target.value as string;
    setSelectedCategory(newCategory);
    setPage(1);
    updateFilters(newCategory, undefined, 1);
    // Announce filter change to screen readers
    announce(`Category filter changed to ${newCategory === 'all' ? 'All Categories' : formatDropdownLabel(newCategory)}`);
  };

  const handleAudienceChange = (event: SelectChangeEvent) => {
    const newAudience = event.target.value as string;
    setSelectedAudience(newAudience);
    setPage(1);
    updateFilters(undefined, newAudience, 1);
    // Announce filter change to screen readers
    announce(`Audience filter changed to ${newAudience === 'all' ? 'All' : formatDropdownLabel(newAudience)}`);
  };

  // Filter products based on selected category and audience
  const filteredProducts = products
    .filter((product: Product) => product.for_sale !== false) // Only show products that are for sale
    .filter((product: Product) => selectedCategory === 'all' || product.category === selectedCategory)
    .filter((product: Product) => selectedAudience === 'all' || product.audience === selectedAudience);

  const categories = Array.from(new Set(products.map((p: Product) => p.category))).filter(Boolean) as string[];
  const audiences = Array.from(new Set(products.map((p: Product) => p.audience))).filter(Boolean) as string[];

  // Announce when products are loaded or filtered
  useEffect(() => {
    if (!loading && filteredProducts.length > 0) {
      announce(`${filteredProducts.length} products found.`);
    } else if (!loading && filteredProducts.length === 0) {
      announce('No products found with the selected filters.');
    }
  }, [filteredProducts, loading, announce]);

  // Pagination derived values
  const totalProducts = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalProducts / ITEMS_PER_PAGE));
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalProducts);
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // Keep page within bounds when filters change significantly
  useEffect(() => {
    if (page > totalPages) {
      setPage(Math.max(1, totalPages));
    }
  }, [page, totalPages]);

  // Announce page changes
  useEffect(() => {
    if (!loading && totalProducts > 0) {
      announce(`Page ${page} of ${totalPages}. Showing ${startIndex + 1} to ${endIndex} of ${totalProducts} products.`);
    }
  }, [page, totalPages, totalProducts, startIndex, endIndex, loading, announce]);

  if (loading) {
    return (
      <Box role="status" aria-live="polite">
        <Typography align="center">Loading products...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box role="alert" aria-live="assertive">
        <Typography color="error" align="center">{error.message}</Typography>
      </Box>
    );
  }

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={seoData.url}
        type={seoData.type}
        structuredData={[breadcrumbData]}
      />
      <Box sx={{ py: 4 }}>
        <Container maxWidth="lg">
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{ fontWeight: 'bold', mb: 4 }}
        >
          Begard Gear
        </Typography>

        <Typography 
          variant="subtitle1" 
          align="center" 
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          Explore our selection of clothing and equipment hand picked to deter bee stings.
        </Typography>

        {/* Filters */}
        <Box sx={{ mb: 4 }} role="search" aria-label="Product filters">
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            sx={{ maxWidth: 800, mx: 'auto', mb: 2 }}
          >
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                label="Category"
                onChange={handleCategoryChange}
                aria-label="Filter by category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category: string) => (
                  <MenuItem key={category} value={category}>{formatDropdownLabel(category)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="audience-select-label">For Who</InputLabel>
              <Select
                labelId="audience-select-label"
                id="audience-select"
                value={selectedAudience}
                label="For Who"
                onChange={handleAudienceChange}
                aria-label="Filter by audience"
              >
                <MenuItem value="all">All</MenuItem>
                {audiences.map((audience: string) => (
                  <MenuItem key={audience} value={audience}>{formatDropdownLabel(audience)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Divider />
        </Box>

        {/* Products Grid */}
        <Grid 
          container 
          spacing={4}
          role="region" 
          aria-label="Products grid"
        >
          {paginatedProducts.map((product: Product) => (
            <Grid item key={product.id} xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to={`/products/${product.id}`}
                state={{ from: `${window.location.pathname}${window.location.search}` }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textDecoration: 'none',
                  color: 'inherit',
                  boxShadow: 1,
                  transition: 'box-shadow 0.2s, background 0.2s, border 0.2s',
                  border: '2px solid transparent',
                  '&:hover': {
                    boxShadow: 6,
                    background: '#FFFDE7',
                    cursor: 'pointer',
                    border: '2px solid #FFEB3B',
                  },
                  '&:focus': {
                    outline: '3px solid #FFEB3B',
                    boxShadow: 6,
                  },
                  position: 'relative',
                }}
                role="article"
                aria-label={`${product.name}${product.begard_approved ? ', Begard Approved' : ''}`}
              >
                <Box
                  sx={{
                    width: '100%',
                    position: 'relative',
                    pt: '100%',
                    bgcolor: '#e0e0e0',
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '6px',
                      bgcolor: '#FFEB3B',
                    }}
                    aria-hidden="true"
                  />
                  {/* Begard Approved Seal with Tooltip */}
                  {product.begard_approved && (
                    <Tooltip 
                      title="Begard Approved: This product deters bees, wasps, hornets, and yellowjackets" 
                      arrow
                      placement="left"
                    >
                      <Box
                        component="img"
                        src={begardSealImage}
                        alt="Begard Approved"
                        role="img"
                        aria-label="Begard Approved: This product deters bees, wasps, hornets, and yellowjackets"
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          width: 70,
                          height: 70,
                          zIndex: 1,
                          filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.2))',
                          cursor: 'help',
                        }}
                      />
                    </Tooltip>
                  )}
                  <CardMedia
                    component="img"
                    image={product.thumbnail_url}
                    alt={`${product.name} product image`}
                    loading="lazy"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h2">
                    {product.name}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        {totalProducts > 0 && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_event: unknown, value: number) => {
                setPage(value);
                updateFilters(undefined, undefined, value);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              color="primary"
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
              aria-label="Products pagination"
              getItemAriaLabel={(type, pageNum, selected) => {
                if (type === 'page') return `Go to page ${pageNum}${selected ? ', current page' : ''}`;
                if (type === 'next') return 'Go to next page';
                if (type === 'previous') return 'Go to previous page';
                if (type === 'first') return 'Go to first page';
                if (type === 'last') return 'Go to last page';
                return '';
              }}
            />
          </Box>
        )}

        {filteredProducts.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }} role="status" aria-live="polite">
            <Typography variant="h6">
              No products found in this category.
            </Typography>
          </Box>
        )}
        </Container>
      </Box>
    </>
  );
};

export default Products; 